// Painting a board off the main thread (docs/186, performance): one shared module Web Worker (paint.worker.ts) paints
// and cuts boards for every scene; each call gets the board ready to upload (`Prepared`). With no Worker
// (tests, old engines) or once the worker has failed, the same two steps run here, synchronously, from the
// page's own copy of the set.
import atlas from 'virtual:fk-encounter-atlas';
import { paintBoard, type BoardIn } from './paint';
import { prepareBoard, type Prepared } from './prepare';
import { loadSet } from './atlas';
import type { PaintReply, PaintRequest } from './paint.worker';
import type { EncounterSet, SetRecord } from './types';
import { borrowsFromCountry } from './paint/context';
import { boardNeedsCountry, countrySetFor } from './sets';

let worker: Worker | undefined;
let broken = !(typeof Worker !== 'undefined' && typeof window !== 'undefined');
let serial = 0;
const waiting = new Map<number, { resolve: (p: PaintReply) => void; reject: (e: unknown) => void }>();

function workerOf(): Worker | undefined {
  if (broken) return undefined;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./paint.worker.ts', import.meta.url), { type: 'module', name: 'fk-board-painter' });
  } catch (e) {
    broken = true;
    console.warn('encounter art: no board painter worker; painting on the main thread', e);
    return undefined;
  }
  worker.onmessage = (e: MessageEvent<PaintReply>) => {
    const w = waiting.get(e.data.job);
    if (!w) return;
    waiting.delete(e.data.job);
    if ('error' in e.data) w.reject(new Error(e.data.error));
    else w.resolve(e.data);
  };
  worker.onerror = (e) => {
    // the worker failed to load or crashed: everything waiting (and everything after) paints here
    console.warn('encounter art: the board painter worker failed; painting on the main thread', e.message);
    e.preventDefault?.();
    broken = true;
    worker?.terminate(); worker = undefined;
    for (const w of waiting.values()) w.reject(new Error('painter worker failed'));
    waiting.clear();
  };
  return worker;
}

const absolute = (urls: { json: string; png: string }) => ({ json: new URL(urls.json, location.href).href, png: new URL(urls.png, location.href).href });

function ask(w: Worker, req: PaintRequest): Promise<PaintReply> {
  return new Promise<PaintReply>((resolve, reject) => {
    waiting.set(req.job, { resolve, reject });
    w.postMessage(req);
  });
}

/**
 * A set's record and atlas fetched and decoded by the worker (which paints from the same copy), or
 * undefined with no worker: the set's files are fetched once for both threads.
 */
export function setFilesViaWorker(id: string): Promise<{ record: SetRecord; data: Uint8ClampedArray; width: number }> | undefined {
  const urls = atlas.sets[id];
  const w = urls ? workerOf() : undefined;
  if (!w || !urls) return undefined;
  return ask(w, { job: ++serial, kind: 'set', id, urls: absolute(urls) }).then((r) => {
    if (!('record' in r)) throw new Error(`no set ${id} from the painter`);
    return r;
  });
}

/**
 * The country set a place's board borrows its water, rough and thicket from (docs/186 EA14), loaded, or undefined:
 * not a place, nothing to borrow, no square that needs it, or it failed to load (the squares then draw as floor).
 */
export async function countryOf(board: BoardIn, set: EncounterSet, load: (id: string) => Promise<EncounterSet> = loadSet): Promise<EncounterSet | undefined> {
  if (!borrowsFromCountry(set) || !boardNeedsCountry(board)) return undefined;
  const id = countrySetFor(set.id, board.country);
  return id ? load(id).catch((e: unknown) => { console.warn(`encounter art: no country set ${id} for ${set.id}`, e); return undefined; }) : undefined;
}

/** On the main thread: load (or reuse) the set and its country's, paint and cut. */
export async function paintHere(board: BoardIn, id: string, chunk: number): Promise<Prepared> {
  const set = await loadSet(id);
  return paintNow(board, set, chunk, await countryOf(board, set));
}

/** Synchronously, from a loaded set (and the country's, for a place: EA14). */
export function paintNow(board: BoardIn, set: Parameters<typeof paintBoard>[1], chunk: number, country?: EncounterSet): Prepared {
  const t0 = performance.now();
  const painted = paintBoard(board, set, country ? { country } : {});
  return prepareBoard(painted, set, chunk, performance.now() - t0);
}

/** Whether boards are painted off the main thread. */
export function painterWorks(): boolean { return !broken; }

/**
 * `board` painted in set `id` and cut into `chunk` px chunks, in the worker when there is one. Resolves
 * undefined when a newer board overtook this one in the worker's queue. `board` is copied (structured clone).
 */
export function paintBoardAsync(board: BoardIn, id: string, chunk: number): Promise<Prepared | undefined> {
  const urls = atlas.sets[id];
  const w = urls ? workerOf() : undefined;
  if (!w || !urls) return paintHere(board, id, chunk);
  // the country's set goes along for a place (EA14); the worker loads it only if the place's set lacks what the board needs
  const cid = boardNeedsCountry(board) ? countrySetFor(id, board.country) : undefined;
  const curls = cid ? atlas.sets[cid] : undefined;
  const req: PaintRequest = {
    job: ++serial, kind: 'paint', id, urls: absolute(urls), chunk,
    board: { w: board.w, h: board.h, tiles: board.tiles, ...(board.seed !== undefined ? { seed: board.seed } : {}), ...(board.country ? { country: board.country } : {}), ...(board.look ? { look: board.look } : {}) },
    ...(cid && curls ? { country: { id: cid, urls: absolute(curls) } } : {}),
  };
  return ask(w, req).then((r) => ('prepared' in r ? r.prepared : undefined)).catch((e: unknown) => {
    console.warn(`encounter art: painting ${id} in the worker failed; painting on the main thread`, e);
    return paintHere(board, id, chunk);
  });
}

/**
 * Started with the page (docs/186, performance): a worker made when the first fight asks for it waited ~3 s for its
 * script behind the scene's own asset loads, and the art with it. Not for `?oryx=1`, nor with no window.
 */
export function warmPainter(): void {
  try { if (new URLSearchParams(window.location.search).get('oryx') === '1') return; } catch { return; }
  workerOf();
}
if (typeof window !== 'undefined' && !broken) warmPainter();
